import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Download, TrendingUp, TrendingDown, Minus, Sparkles, RefreshCw, Share2, Check } from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoDark } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const ZONE_COLORS = ["#E24B4A", "#EF9F27", "#CBCB2A", "#639922", "#C9A84C"];
const STATUS_STYLES = {
  improved: { bg: "#E8F5E9", fg: "#2E7D32", icon: TrendingUp, label: "Improved" },
  stalled: { bg: "#FFF3E0", fg: "#E65100", icon: Minus, label: "Stalled" },
  declined: { bg: "#FFEBEE", fg: "#C62828", icon: TrendingDown, label: "Declined" },
};

export function HeadlineCard({ headline }) {
  const delta = headline.delta || 0;
  const deltaColor = delta > 0 ? "#2E7D32" : delta < 0 ? "#C62828" : "#E65100";
  const arrow = delta > 0 ? "↑" : delta < 0 ? "↓" : "→";
  return (
    <div className="bg-navy text-cream rounded-2xl p-8 mb-6" data-testid="movement-headline">
      <p className="font-dm text-gold text-xs uppercase tracking-wider mb-3">Capability Movement</p>
      <h1 className="font-playfair text-4xl sm:text-5xl font-bold mb-4" data-testid="movement-headline-label">{headline.label}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
        <div>
          <p className="font-dm text-cream/60 text-xs uppercase tracking-wide mb-1">Present Fit (Before)</p>
          <p className="font-playfair text-4xl font-bold">{headline.present_fit_then}%</p>
        </div>
        <div>
          <p className="font-dm text-cream/60 text-xs uppercase tracking-wide mb-1">Present Fit (Now)</p>
          <p className="font-playfair text-4xl font-bold text-gold">{headline.present_fit_now}%</p>
        </div>
        <div>
          <p className="font-dm text-cream/60 text-xs uppercase tracking-wide mb-1">Movement</p>
          <p className="font-playfair text-4xl font-bold" style={{ color: deltaColor }} data-testid="movement-delta">
            {arrow} {delta > 0 ? "+" : ""}{delta}%
          </p>
        </div>
      </div>
    </div>
  );
}

function ZoneCell({ zone, label }) {
  if (!zone || zone < 1 || zone > 5) return <span className="text-navy/40">—</span>;
  const color = ZONE_COLORS[zone - 1];
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-3 h-3 rounded-full" style={{ background: color }} />
      <span className="font-dm text-navy text-sm">{label}</span>
    </span>
  );
}

export function ZoneMovementTable({ movements }) {
  return (
    <div className="bg-white rounded-2xl border border-navy/10 p-6 mb-6" data-testid="zone-movement-table">
      <h2 className="font-playfair text-navy text-2xl font-bold mb-4">Skill-by-Skill Movement</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-navy/60 font-dm text-xs uppercase tracking-wide border-b border-navy/10">
              <th className="text-left py-3 pr-4">Skill</th>
              <th className="text-left py-3 px-2">Dimension</th>
              <th className="text-left py-3 px-2">Before</th>
              <th className="text-left py-3 px-2">Now</th>
              <th className="text-left py-3 px-2">Required</th>
              <th className="text-left py-3 px-2">Movement</th>
              <th className="text-left py-3 pl-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {movements.map((m, i) => {
              const sty = STATUS_STYLES[m.status] || STATUS_STYLES.stalled;
              const Icon = sty.icon;
              return (
                <tr key={`${m.skill}-${i}`} className="border-b border-navy/5">
                  <td className="py-3 pr-4 font-dm text-navy">{m.skill}</td>
                  <td className="py-3 px-2 font-dm text-navy/70 text-xs">{m.dimension}</td>
                  <td className="py-3 px-2"><ZoneCell zone={m.from_zone} label={m.from_label} /></td>
                  <td className="py-3 px-2"><ZoneCell zone={m.to_zone} label={m.to_label} /></td>
                  <td className="py-3 px-2 font-dm text-navy/70">{m.required_label}</td>
                  <td className="py-3 px-2 font-dm font-bold" style={{ color: sty.fg }}>{m.movement}</td>
                  <td className="py-3 pl-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-dm text-xs font-bold"
                      style={{ background: sty.bg, color: sty.fg }}>
                      <Icon size={12} /> {sty.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InterventionSummary({ summary }) {
  const completed = summary.interventions_completed || [];
  return (
    <div className="bg-white rounded-2xl border border-navy/10 p-6 mb-6" data-testid="intervention-summary">
      <h2 className="font-playfair text-navy text-2xl font-bold mb-4">Intervention Summary</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Stat label="Skills Addressed" value={summary.addressed} color="#0F1E35" />
        <Stat label="Improved" value={summary.improved} color="#2E7D32" />
        <Stat label="Stalled" value={summary.stalled} color="#E65100" />
        <Stat label="Declined" value={summary.declined} color="#C62828" />
      </div>
      {completed.length > 0 && (
        <div>
          <p className="font-dm text-navy/60 text-xs uppercase tracking-wide mb-3">Interventions Completed</p>
          <ul className="space-y-2">
            {completed.map((c, i) => {
              const sty = STATUS_STYLES[c.status] || STATUS_STYLES.stalled;
              return (
                <li key={`${c.skill}-${i}`} className="flex items-start gap-3 py-2 border-b border-navy/5 last:border-0">
                  <span className="font-dm text-xs font-bold uppercase px-2 py-0.5 rounded mt-0.5"
                    style={{ background: sty.bg, color: sty.fg }}>{sty.label}</span>
                  <div className="flex-1">
                    <p className="font-dm text-navy"><strong>{c.skill}</strong></p>
                    <p className="font-dm text-navy/60 text-xs">
                      {c.intervention} · planned {c.timeline_planned}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center bg-cream rounded-xl py-4">
      <p className="font-playfair text-3xl font-bold" style={{ color }}>{value}</p>
      <p className="font-dm text-navy/60 text-xs uppercase tracking-wide mt-1">{label}</p>
    </div>
  );
}

export function NarrativeSection({ title, body, testid }) {
  if (!body) return null;
  // split paragraphs on blank lines
  const paras = body.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="bg-cream/70 rounded-2xl p-6 mb-6 border-l-4 border-gold" data-testid={testid}>
      <h2 className="font-playfair text-navy text-2xl font-bold mb-3 flex items-center gap-2">
        <Sparkles size={20} className="text-gold" /> {title}
      </h2>
      {paras.map((p, i) => (
        <p key={i} className="font-dm text-navy/80 leading-relaxed mb-3 last:mb-0">{p}</p>
      ))}
    </div>
  );
}

export function downloadMovementReportPdf(report) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    let y = 40;
    doc.setFillColor(15, 30, 53);
    doc.rect(0, 0, W, 70, "F");
    doc.setFontSize(18); doc.setTextColor(201, 168, 76);
    doc.text("RoleIQ", 40, 35);
    doc.setFontSize(10); doc.setTextColor(255, 255, 255);
    doc.text("Capability Movement Report", 40, 55);
    y = 100;
    doc.setTextColor(15, 30, 53); doc.setFontSize(16);
    doc.text(`${report.employee?.name || "Employee"} — ${report.job_role || ""}`, 40, y); y += 24;
    doc.setFontSize(11); doc.setTextColor(70, 70, 70);
    doc.text(`Present Fit: ${report.headline?.present_fit_then}%  →  ${report.headline?.present_fit_now}%  (${report.headline?.delta >= 0 ? "+" : ""}${report.headline?.delta}%)`, 40, y); y += 18;
    doc.text(`Label: ${report.headline?.label}`, 40, y); y += 24;

    doc.setFontSize(13); doc.setTextColor(15, 30, 53);
    doc.text("Skill Movement", 40, y); y += 18;
    doc.setFontSize(10); doc.setTextColor(70, 70, 70);
    (report.zone_movements || []).slice(0, 16).forEach((m) => {
      const line = `${m.skill}: ${m.from_label} → ${m.to_label}  (${m.movement} · ${m.status})`;
      doc.text(line.slice(0, 95), 40, y); y += 14;
      if (y > 760) { doc.addPage(); y = 60; }
    });
    y += 12;
    doc.setFontSize(13); doc.setTextColor(15, 30, 53);
    doc.text("Movement Narrative", 40, y); y += 18;
    doc.setFontSize(10); doc.setTextColor(70, 70, 70);
    const wrap = doc.splitTextToSize(report.ai_narrative || "", W - 80);
    wrap.forEach((ln) => { doc.text(ln, 40, y); y += 13; if (y > 760) { doc.addPage(); y = 60; } });
    y += 8;
    doc.setFontSize(13); doc.setTextColor(15, 30, 53);
    doc.text("Business Translation", 40, y); y += 18;
    doc.setFontSize(10); doc.setTextColor(70, 70, 70);
    const wrap2 = doc.splitTextToSize(report.business_translation || "", W - 80);
    wrap2.forEach((ln) => { doc.text(ln, 40, y); y += 13; if (y > 760) { doc.addPage(); y = 60; } });

    doc.save(`movement-report-${report.employee?.name || "employee"}.pdf`);
}

export default function MovementReportPage() {
  usePageTitle("Capability Movement Report");
  const { report_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const pdfRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/movement-report/${report_id}`, { withCredentials: true });
        setReport(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load movement report");
      } finally {
        setLoading(false);
      }
    })();
  }, [report_id]);

  const handlePdf = () => { if (report) downloadMovementReportPdf(report); };

  const handleShare = async () => {
    setShareBusy(true);
    setCopied(false);
    try {
      const r = await axios.post(`${API}/api/movement-report/${report_id}/share`, { expires_days: 30 }, { withCredentials: true });
      const url = r.data?.public_url?.startsWith("http") ? r.data.public_url : `${window.location.origin}${r.data.public_url}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch { /* clipboard may be unavailable; link is still displayed */ }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setShareBusy(false);
    }
  };

  if (loading) return <div className="font-dm p-12 text-center text-navy/70">Loading movement report…</div>;
  if (error) return (
    <div className="font-dm p-12 text-center">
      <p className="text-red-700 mb-3" data-testid="movement-error">{error}</p>
      <button onClick={() => navigate(-1)} className="text-gold underline">Back</button>
    </div>
  );
  if (!report) return null;

  return (
    <div className="bg-cream min-h-screen pb-16">
      {/* Brand header */}
      <div className="bg-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center"><LogoDark width={140} height={40} /></Link>
          <Link to="/dashboard" className="font-dm text-cream/70 text-sm flex items-center gap-1 hover:text-gold">
            <ArrowLeft size={16} /> Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8" ref={pdfRef}>
        {/* Section 1 — Headline */}
        <HeadlineCard headline={report.headline || {}} />

        {/* Section 2 — Zone Movement Table */}
        <ZoneMovementTable movements={report.zone_movements || []} />

        {/* Section 3 — Intervention Summary */}
        <InterventionSummary summary={report.intervention_summary || {}} />

        {/* Section 4 — AI Movement Narrative */}
        <NarrativeSection title="Movement Narrative" body={report.ai_narrative} testid="ai-narrative" />

        {/* Section 5 — Business Translation */}
        <NarrativeSection title="Business Translation" body={report.business_translation} testid="business-translation" />

        {/* Section 6 — Footer */}
        <div className="bg-white border border-navy/10 rounded-2xl p-6 text-center" data-testid="movement-footer">
          <p className="font-dm text-navy/60 text-xs uppercase tracking-wide mb-2">Powered by</p>
          <LogoDark width={120} height={34} />
          <p className="font-dm text-navy/50 text-xs mt-3">
            Generated {new Date(report.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            {" · "} 1 Intelligence Token used
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
            <button onClick={handlePdf} data-testid="movement-pdf-btn"
              className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-2.5 rounded-lg font-dm font-semibold hover:bg-navy/90">
              <Download size={16} /> Download PDF
            </button>
            <button onClick={handleShare} disabled={shareBusy} data-testid="movement-share-btn"
              className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-2.5 rounded-lg font-dm font-semibold hover:bg-navy hover:text-cream disabled:opacity-50">
              {copied ? <Check size={16} /> : <Share2 size={16} />} {shareBusy ? "Creating link…" : copied ? "Link copied!" : "Share board-ready link"}
            </button>
            <Link to={`/capscan/report/${report.rescan_report_id}`} data-testid="back-to-rescan-btn"
              className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 rounded-lg font-dm font-semibold hover:bg-gold/90">
              <RefreshCw size={16} /> Back to Re-scan
            </Link>
          </div>
          {shareUrl && (
            <div className="mt-4 px-4 py-3 bg-cream rounded-lg border border-navy/10" data-testid="movement-share-url">
              <p className="font-dm text-navy/60 text-xs uppercase tracking-wide mb-1">Shareable link (read-only, expires in 30 days)</p>
              <p className="font-mono text-navy text-xs break-all">{shareUrl}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
